import { useState, createContext, useEffect, useContext } from "react";
import { loadGlobalConfig } from "../config";
import apiClient from "../apis/ApiInterceptor";
import { jwtDecode } from "jwt-decode";

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext); // Custom hook to consume AuthContext

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading, false = not authenticated, true = authenticated
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuthentication = async () => {
    const accessToken = localStorage.getItem("access_token");

    if (!accessToken) {
      setIsAuthenticated(false);
      setAuthenticatedUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.get("/auth/verify"); // Authorization header is set in ApiInterceptor
      if (response.status === 200) {
        setIsAuthenticated(true);
        const decodedToken = jwtDecode(accessToken);
        const loggedUserId = decodedToken.sub;
        setAuthenticatedUser(loggedUserId);
        await loadGlobalConfig();
      } else {
        setIsAuthenticated(false);
        setAuthenticatedUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Exact path check for public routes
    const publicPaths = ["/login", "/register", "/plans", "/"];
    const currentPath = window.location.pathname;

    // Regex
    const isAgreementsPublic = /^\/agreements\/[^/]+\/users\/\d+$/.test(currentPath);
    const isResetPasswordPath = /^\/reset-password(\/.*)?$/.test(currentPath);

    const isPublicPath =
      publicPaths.includes(currentPath) || isAgreementsPublic || isResetPasswordPath;
    if (isPublicPath) {
      // Do not verify token on public pages.
      setLoading(false);
      return;
    }

    checkAuthentication();
  }, []);

  const updateAuthentication = (authenticated, authenticatedUser) => {
    setIsAuthenticated(authenticated);
    setAuthenticatedUser(authenticatedUser);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, authenticatedUser, loading, updateAuthentication }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;